import { noop } from "lodash-es";
import { memo, useId } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { ChevronRight, Lock } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import SearchHighlight from "components/SearchHighlight";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import DropdownMenuDivider from "ds/components/DropdownMenu/Divider";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import DropdownMenuSection from "ds/components/DropdownMenu/Section";
import DropdownMenuSectionHeader from "ds/components/DropdownMenu/Section/Header";
import Icon from "ds/components/Icon";
import TextEllipsis from "ds/components/TextEllipsis";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import useHasSpaceAdminAccess from "shared/Space/useHasSpaceAdminAccess";
import { isLegacySpace, isRootSpace } from "utils/space";
import { TreeNode } from "utils/tree";
import { withTestId } from "utils/withTestId";

import { SpacesViewActionsContext } from "../../ActionsContext";
import { showSpaceDrawer } from "../../SpacesDrawer";
import { SpacesDrawerWriteMode, SpaceWithParentAccessLevel } from "../../types";
import { isPredefinedSpace } from "../../utils";
import {
  COLUMN_GAP,
  COLUMN_ORDER,
  LEVEL_MARGIN,
  SPACE_TREE_LIST_ROOT_SPACE_USERFLOW_ID,
  SPACE_TREE_LIST_ROW_TEST_ID,
} from "./constants";

type SpacesTreeGridListRowProps = {
  node: TreeNode<SpaceWithParentAccessLevel>;
  isActive: boolean;
  isExpanded: boolean;
  isRootAdmin: boolean;
  searchQuery?: string;
  onToggle: () => void;
  canNonRootAdminCreateSpace: boolean;
};

const SpacesTreeGridListRow = ({
  isActive,
  isExpanded,
  isRootAdmin,
  searchQuery,
  onToggle,
  node,
  canNonRootAdminCreateSpace,
}: SpacesTreeGridListRowProps) => {
  const id = useId();
  const { onFilter, onDelete, onCreate } = useTypedContext(SpacesViewActionsContext);
  const rowLevel = node.path.length;

  const getRowMargin = (isExpandable: boolean) => {
    const level = rowLevel - 1;
    const expandable = isExpandable ? "0px" : LEVEL_MARGIN;
    const res = `calc(${LEVEL_MARGIN} * ${level} + ${expandable})`;
    return res;
  };

  const handleCreate = () => {
    onCreate(node.id);
    if (!isExpanded) {
      onToggle();
    }
  };

  const handleDelete = () => {
    onDelete(node.id);
  };

  const hasAccessToParentSpace = useHasSpaceAdminAccess(node.item.parentSpace || undefined);

  const canDeleteSpace =
    (isRootAdmin || (canNonRootAdminCreateSpace && hasAccessToParentSpace)) &&
    !isPredefinedSpace(node.id) &&
    node.children?.length === 0;

  const hasAccessToSpace = useHasSpaceAdminAccess(node.item.id);

  const canAddChildSpace = isRootAdmin || (canNonRootAdminCreateSpace && hasAccessToSpace);

  const canEditSpace = isRootAdmin || (canNonRootAdminCreateSpace && hasAccessToParentSpace);

  const isEditable = canEditSpace && !isPredefinedSpace(node.id);

  const handleSelect = () => {
    showSpaceDrawer({
      space: node.item,
      drawerWriteMode: isEditable ? SpacesDrawerWriteMode.Edit : undefined,
      isRootAdmin,
      onClose: noop,
    });
  };

  return (
    <ListEntitiesItem
      id={id}
      key={node.id}
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      isActive={isActive}
      {...withTestId(SPACE_TREE_LIST_ROW_TEST_ID)}
      role="row"
      aria-level={rowLevel}
      aria-expanded={isExpanded}
    >
      <Box
        gap="medium"
        align="center"
        style={{ marginLeft: getRowMargin(node.children && node.children.length > 0) }}
        role="rowheader"
      >
        {node.children && node.children.length > 0 && (
          <Box shrink="0">
            <ButtonIcon
              icon={ChevronRight}
              iconRotate={isExpanded ? "90" : undefined}
              aria-expanded={isExpanded}
              aria-controls={id}
              onPress={onToggle}
              variant="ghost"
            >
              {isExpanded ? "Collapse" : "Expand"}
            </ButtonIcon>
          </Box>
        )}
        <ListEntitiesItemLink
          title={node.name}
          data-userflow-id={
            isRootSpace(node.id) ? SPACE_TREE_LIST_ROOT_SPACE_USERFLOW_ID : undefined
          }
          href={`/spaces/${node.id}`}
          searchQuery={searchQuery}
        />
        {isPredefinedSpace(node.id) && isRootAdmin && (
          <Tooltip mode="parent" on={(tooltipProps) => <Icon {...tooltipProps} src={Lock} />}>
            This space is not editable.
          </Tooltip>
        )}
      </Box>
      <Box direction="row" align="center" shrink="0" role="gridcell">
        <TextEllipsis tooltip={node.id}>
          {(props) => (
            <Typography {...props} variant="p-body3" tag="span">
              {searchQuery ? (
                <SearchHighlight fullText={node.id} searchQuery={searchQuery} />
              ) : (
                node.id
              )}
            </Typography>
          )}
        </TextEllipsis>
      </Box>
      <Box direction="row" align="center" shrink="0" role="gridcell">
        <Typography tag="span" variant="p-body3">
          {node.item.inheritEntities ? "Yes" : "No"}
        </Typography>
      </Box>
      <Box direction="row" align="center" shrink="0" role="gridcell">
        <DropdownMenuEllipsis tooltip="Space actions">
          {canAddChildSpace && !isLegacySpace(node.id) && (
            <DropdownMenuItem onAction={handleCreate}>Add child space</DropdownMenuItem>
          )}

          <DropdownMenuItem onAction={handleSelect}>
            {isEditable ? "Edit details" : "See details"}
          </DropdownMenuItem>

          <CopyFieldDropdownMenuItem title="Copy ID" value={node.id} />

          {canDeleteSpace && (
            <DropdownMenuItem onAction={handleDelete} danger>
              Delete
            </DropdownMenuItem>
          )}
          <DropdownMenuDivider />
          <DropdownMenuSection>
            <DropdownMenuSectionHeader>Filters</DropdownMenuSectionHeader>
            <DropdownMenuItem onAction={() => onFilter(node.id, "/stacks")}>
              View Stacks
            </DropdownMenuItem>
            <DropdownMenuItem onAction={() => onFilter(node.id, "/blueprints")}>
              View Blueprints
            </DropdownMenuItem>
            <DropdownMenuItem onAction={() => onFilter(node.id, "/integrations/webhooks")}>
              View Webhooks
            </DropdownMenuItem>
          </DropdownMenuSection>
        </DropdownMenuEllipsis>
      </Box>
    </ListEntitiesItem>
  );
};

export default memo(SpacesTreeGridListRow);
